import { CustomerVisibilityType } from '@masterofmalt/atom.componentlibrary/models/internal/CustomerVisibilityGroup';
import { createEnv } from '@t3-oss/env-nextjs';
import { z, ZodType } from 'zod';

import { currencyCodeSchema } from './types/currency';
import { rewriteRouteSchema } from './types/rewrite-route';

let b2bApiTokenSchema;

// if the site type is B2B, the B2B_API_TOKEN is required
if (process.env.NEXT_PUBLIC_SITE_TYPE === 'B2B') {
  b2bApiTokenSchema = z.string().min(1);
} else {
  b2bApiTokenSchema = z.any();
}

const stringBooleanSchema = z.preprocess((val) => val === 'true', z.boolean()).default(false);

const stringNumberSchema = z.preprocess((val) => {
  if (typeof val === 'string') {
    const parsed = parseFloat(val);

    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return val;
}, z.number());

const jsonRecordSchema = z.preprocess(
  (s) => {
    if (typeof s !== 'string') {
      throw new Error('Invalid JSON string');
    }

    return JSON.parse(s);
  },
  z.record(z.string(), z.string()),
);

const jsonArraySchema = <T extends ZodType>(schema: T) =>
  z.preprocess((s) => {
    if (typeof s !== 'string') {
      throw new Error('Invalid JSON string');
    }

    return JSON.parse(s);
  }, z.array(schema));

export const env = createEnv({
  /*
   * Serverside Environment variables, not available on the client.
   * Will throw if you access these variables on the client.
   */
  server: {
    ACCESS_TOKEN_MARKETING_READ_ONLY: z.string().min(1),
    ACCESS_TOKEN_CUSTOMERS_READ_ONLY: z.string().min(1),
    ACCESS_TOKEN_CUSTOMERS_MODIFY: z.string().min(1),
    ACCESS_TOKEN_ORDERS_READ_ONLY: z.string().min(1),
    ACCESS_TOKEN_CUSTOMERS_MODIFY_LOGIN: z.string().min(1),
    ACCESS_TOKEN_CARTS_MODIFY: z.string().min(1),

    CLIENT_SECRET_CUSTOMERS_MODIFY_LOGIN: z.string().length(64),
    CLIENT_ID_CUSTOMERS_MODIFY_LOGIN: z.string().min(1),

    BIGCOMMERCE_CHANNEL_ID: z.string().min(1),
    BIGCOMMERCE_ACCESS_TOKEN: z.string().min(1).nullish(),
    BIGCOMMERCE_STORE_HASH: z.string().min(1),

    BUILDER_PUBLIC_API_KEY: z.string().min(1),
    REVALIDATION_SECRET: z.string().min(10),
    B2B_API_TOKEN: b2bApiTokenSchema,

    GIFT_BOX_OPTION_NAME: z.string(),
    GIFT_BOX_SKU: z.string(),

    ATOM_TOKEN_URL: z.string().url(),
    ATOM_CLIENT_ID: z.string(),
    ATOM_CLIENT_SECRET: z.string(),
    ATOM_API_URL: z.string().url(),

    ORIGIN: z.string().url(),
    ORIGIN_WITHOUT_PROTOCOL: z.string().min(1),

    GIFT_CARD_TEXT_OPTION_NAME: z.string().default('message'),
    GIFT_CARD_SKU: z.string(),

    FF_SSO_LOGIN: stringBooleanSchema,

    ATOM_WAF_GUID: z.string().uuid(),
    ATOM_ROUTE_CONFIG: jsonArraySchema(rewriteRouteSchema),

    MOM_PRIME_CUSTOMER_GROUP_ID: stringNumberSchema,

    AZURE_BLOB_STORAGE_URL: z.string().url(),
    AZURE_STORAGE_ACCOUNT_NAME: z.string().min(1),
    AZURE_STORAGE_ACCOUNT_KEY: z.string().min(1),
    AZURE_STORAGE_CONTAINER_NAME: z.string().min(1),

    MOM_RETAIL_CUSTOMER_GROUP_ID: stringNumberSchema,
    DEFAULT_CUSTOMER_GROUP_ID: stringNumberSchema,
    CUSTOMER_GROUP_VISIBILITY_GROUP_MAP: jsonRecordSchema,

    KLAVIYO_API_KEY: z.string().min(1),

    MINIBC_SECRET: z.string().min(1),
  },
  /*
   * Environment variables available on the client (and server).
   *
   * 💡 You'll get type errors if these are not prefixed with NEXT_PUBLIC_.
   */
  client: {
    NEXT_PUBLIC_ALGOLIA_APPLICATION_ID: z.string().min(1),
    NEXT_PUBLIC_ALGOLIA_PUBLIC_KEY: z.string().min(1),
    NEXT_PUBLIC_ALGOLIA_INDEX_NAME: z.string().min(1),
    NEXT_PUBLIC_ALGOLIA_SUGGESTIONS_INDEX: z.string().min(1),
    NEXT_PUBLIC_ALGOLIA_DISABLE_SECURE_TOKENS: stringBooleanSchema,

    NEXT_PUBLIC_B2B_URL: z.string().url(),
    NEXT_PUBLIC_D2C_URL: z.string().url(),

    NEXT_PUBLIC_IMAGE_PROXY_URL: z.string().url(),

    NEXT_PUBLIC_SITE_TYPE: z.enum(['D2C', 'B2B']),
    NEXT_PUBLIC_DEFAULT_CUSTOMER_VISIBILITY_GROUP: z.enum([
      'Consumer',
      'OffTrade',
      'OnTrade',
      'AllTrade',
    ]) satisfies ZodType<CustomerVisibilityType>,

    NEXT_PUBLIC_GRAPHQL_API_DOMAIN: z.string(),
    NEXT_PUBLIC_STORE_HASH: z.string().min(1),
    NEXT_PUBLIC_STORE_CHANNEL_ID: stringNumberSchema,

    NEXT_PUBLIC_E_GIFT_VOUCHER_SKU_BASE: z.string().min(1),
    NEXT_PUBLIC_PHYSICAL_GIFT_VOUCHER_SKU_BASE: z.string().min(1),

    NEXT_PUBLIC_KLAVIYO_COMPANY_ID: z.string().min(1),
    NEXT_PUBLIC_KLAVIYO_LIST_ID: z.string().min(1),

    NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: z.string().nullish(),

    NEXT_PUBLIC_DEFAULT_CURRENCY_CODE: currencyCodeSchema,

    NEXT_PUBLIC_DEFAULT_PRICE_LIST_ID: stringNumberSchema,

    NEXT_PUBLIC_MINIBC_APP_ID: z.string().uuid(),
    NEXT_PUBLIC_MINIBC_APP_CLIENT_ID: z.string().min(1),
    NEXT_PUBLIC_MINIBC_STORE_ID: z.string().min(1),
    NEXT_PUBLIC_MINIBC_STORE_TOKEN: z.string().min(1),
  },
  /*
   * Due to how Next.js bundles environment variables on Edge and Client,
   * we need to manually destructure them to make sure all are included in bundle.
   *
   * 💡 You'll get type errors if not all variables from `server` & `client` are included here.
   */
  runtimeEnv: {
    // Scope: Marketing read-only
    ACCESS_TOKEN_MARKETING_READ_ONLY: process.env.ACCESS_TOKEN_MARKETING_READ_ONLY,
    // Scope: Customers read-only
    ACCESS_TOKEN_CUSTOMERS_READ_ONLY: process.env.ACCESS_TOKEN_CUSTOMERS_READ_ONLY,
    // Scope: Customers modify
    ACCESS_TOKEN_CUSTOMERS_MODIFY: process.env.ACCESS_TOKEN_CUSTOMERS_MODIFY,
    // Scope: Orders read-only
    ACCESS_TOKEN_ORDERS_READ_ONLY: process.env.ACCESS_TOKEN_ORDERS_READ_ONLY,
    // Scope: Customers modify, Customers login
    ACCESS_TOKEN_CUSTOMERS_MODIFY_LOGIN: process.env.ACCESS_TOKEN_CUSTOMERS_MODIFY_LOGIN,
    // Scope: Carts modify
    ACCESS_TOKEN_CARTS_MODIFY: process.env.ACCESS_TOKEN_CARTS_MODIFY,

    CLIENT_SECRET_CUSTOMERS_MODIFY_LOGIN: process.env.CLIENT_SECRET_CUSTOMERS_MODIFY_LOGIN,
    CLIENT_ID_CUSTOMERS_MODIFY_LOGIN: process.env.CLIENT_ID_CUSTOMERS_MODIFY_LOGIN,

    BIGCOMMERCE_CHANNEL_ID: process.env.BIGCOMMERCE_CHANNEL_ID,
    BIGCOMMERCE_ACCESS_TOKEN: process.env.BIGCOMMERCE_ACCESS_TOKEN,
    BIGCOMMERCE_STORE_HASH: process.env.BIGCOMMERCE_STORE_HASH,

    BUILDER_PUBLIC_API_KEY: process.env.BUILDER_PUBLIC_API_KEY,
    REVALIDATION_SECRET: process.env.REVALIDATION_SECRET,
    B2B_API_TOKEN: process.env.B2B_API_TOKEN,
    GIFT_BOX_OPTION_NAME: process.env.GIFT_BOX_OPTION_NAME,
    GIFT_BOX_SKU: process.env.GIFT_BOX_SKU,
    GIFT_CARD_SKU: process.env.GIFT_CARD_SKU,
    GIFT_CARD_TEXT_OPTION_NAME: process.env.GIFT_CARD_TEXT_OPTION_NAME,
    ORIGIN: process.env.ORIGIN,
    ORIGIN_WITHOUT_PROTOCOL: process.env.ORIGIN_WITHOUT_PROTOCOL,
    NEXT_PUBLIC_DEFAULT_CURRENCY_CODE: process.env.NEXT_PUBLIC_DEFAULT_CURRENCY_CODE,

    FF_SSO_LOGIN: process.env.FF_SSO_LOGIN,

    NEXT_PUBLIC_ALGOLIA_APPLICATION_ID: process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID,
    NEXT_PUBLIC_ALGOLIA_PUBLIC_KEY: process.env.NEXT_PUBLIC_ALGOLIA_PUBLIC_KEY,
    NEXT_PUBLIC_ALGOLIA_INDEX_NAME: process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME,
    NEXT_PUBLIC_ALGOLIA_SUGGESTIONS_INDEX: process.env.NEXT_PUBLIC_ALGOLIA_SUGGESTIONS_INDEX,
    NEXT_PUBLIC_ALGOLIA_DISABLE_SECURE_TOKENS:
      process.env.NEXT_PUBLIC_ALGOLIA_DISABLE_SECURE_TOKENS,

    NEXT_PUBLIC_SITE_TYPE: process.env.NEXT_PUBLIC_SITE_TYPE,
    NEXT_PUBLIC_DEFAULT_CUSTOMER_VISIBILITY_GROUP:
      process.env.NEXT_PUBLIC_DEFAULT_CUSTOMER_VISIBILITY_GROUP,

    NEXT_PUBLIC_B2B_URL: process.env.NEXT_PUBLIC_B2B_URL,
    NEXT_PUBLIC_D2C_URL: process.env.NEXT_PUBLIC_D2C_URL,

    ATOM_TOKEN_URL: process.env.ATOM_TOKEN_URL,
    ATOM_CLIENT_ID: process.env.ATOM_CLIENT_ID,
    ATOM_CLIENT_SECRET: process.env.ATOM_CLIENT_SECRET,
    ATOM_API_URL: process.env.ATOM_API_URL,
    ATOM_WAF_GUID: process.env.ATOM_WAF_GUID,
    ATOM_ROUTE_CONFIG: process.env.ATOM_ROUTE_CONFIG,

    NEXT_PUBLIC_GRAPHQL_API_DOMAIN: process.env.NEXT_PUBLIC_GRAPHQL_API_DOMAIN,
    NEXT_PUBLIC_STORE_HASH: process.env.NEXT_PUBLIC_STORE_HASH,
    NEXT_PUBLIC_STORE_CHANNEL_ID: process.env.NEXT_PUBLIC_STORE_CHANNEL_ID,

    NEXT_PUBLIC_E_GIFT_VOUCHER_SKU_BASE: process.env.NEXT_PUBLIC_E_GIFT_VOUCHER_SKU_BASE,
    NEXT_PUBLIC_PHYSICAL_GIFT_VOUCHER_SKU_BASE:
      process.env.NEXT_PUBLIC_PHYSICAL_GIFT_VOUCHER_SKU_BASE,

    NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,
    NEXT_PUBLIC_KLAVIYO_COMPANY_ID: process.env.NEXT_PUBLIC_KLAVIYO_COMPANY_ID,
    NEXT_PUBLIC_KLAVIYO_LIST_ID: process.env.NEXT_PUBLIC_KLAVIYO_LIST_ID,

    NEXT_PUBLIC_IMAGE_PROXY_URL: process.env.NEXT_PUBLIC_IMAGE_PROXY_URL,

    KLAVIYO_API_KEY: process.env.KLAVIYO_API_KEY,

    MOM_PRIME_CUSTOMER_GROUP_ID: process.env.MOM_PRIME_CUSTOMER_GROUP_ID,

    AZURE_BLOB_STORAGE_URL: process.env.AZURE_BLOB_STORAGE_URL,
    AZURE_STORAGE_ACCOUNT_NAME: process.env.AZURE_STORAGE_ACCOUNT_NAME,
    AZURE_STORAGE_ACCOUNT_KEY: process.env.AZURE_STORAGE_ACCOUNT_KEY,
    AZURE_STORAGE_CONTAINER_NAME: process.env.AZURE_STORAGE_CONTAINER_NAME,

    MOM_RETAIL_CUSTOMER_GROUP_ID: process.env.MOM_RETAIL_CUSTOMER_GROUP_ID,
    DEFAULT_CUSTOMER_GROUP_ID: process.env.DEFAULT_CUSTOMER_GROUP_ID,
    CUSTOMER_GROUP_VISIBILITY_GROUP_MAP: process.env.CUSTOMER_GROUP_VISIBILITY_GROUP_MAP,

    NEXT_PUBLIC_DEFAULT_PRICE_LIST_ID: process.env.NEXT_PUBLIC_DEFAULT_PRICE_LIST_ID,

    NEXT_PUBLIC_MINIBC_APP_ID: process.env.NEXT_PUBLIC_MINIBC_APP_ID,
    NEXT_PUBLIC_MINIBC_APP_CLIENT_ID: process.env.NEXT_PUBLIC_MINIBC_APP_CLIENT_ID,
    MINIBC_SECRET: process.env.MINIBC_SECRET,
    NEXT_PUBLIC_MINIBC_STORE_ID: process.env.NEXT_PUBLIC_MINIBC_STORE_ID,
    NEXT_PUBLIC_MINIBC_STORE_TOKEN: process.env.NEXT_PUBLIC_MINIBC_STORE_TOKEN,
  },
});
